import { Component, OnInit, ViewChild, inject, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AddFormComponent, ActionButton, FormField } from '@shared/components/add-form/add-form.component';
import { ProductPricingService } from '../services/product-pricing.service';
import { PriceItem, PRICE_CODE_OPTIONS } from '../product-pricing.config';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

@Component({
  selector: 'app-product-pricing-edit',
  standalone: true,
  imports: [CommonModule, AddFormComponent],
  templateUrl: './product-pricing-edit.component.html',
  styleUrls: ['./product-pricing-edit.component.css']
})
export class ProductPricingEditComponent implements OnInit, AfterViewInit, CanComponentDeactivate {
  @ViewChild(AddFormComponent) addForm!: AddFormComponent;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private service = inject(ProductPricingService);
  private translateService = inject(TranslateService);

  title = 'goods.pricing.form.edit.title';
  loading = false;
  private skipCanDeactivate = false;
  initialData: any = null;

  headerActions: ActionButton[] = [
    { label: 'goods.pricing.form.actions.cancel', action: 'cancel', outlined: true, severity: 'secondary' },
    { label: 'goods.pricing.form.actions.save', action: 'save', severity: 'info' }
  ];

  formFields: FormField[] = [
    {
      label: 'goods.pricing.form.fields.price_code',
      controlName: 'value',
      type: 'dropdown',
      placeholder: 'goods.pricing.form.placeholders.select_price_code',
      options: [],
      required: true,
      section: 'goods.pricing.form.sections.basic_information'
    },
    {
      label: 'goods.pricing.form.fields.standard_price_type_derived',
      controlName: 'valueMeaning',
      type: 'text',
      placeholder: '',
      readonly: true,
      disabled: true,
      section: 'goods.pricing.form.sections.basic_information'
    },
    {
      label: 'goods.pricing.form.fields.marketplace_price_name',
      controlName: 'meaning',
      type: 'text',
      placeholder: 'goods.pricing.form.placeholders.enter_marketplace_price_name',
      required: true,
      maxLength: 200,
      section: 'goods.pricing.form.sections.basic_information'
    },
    {
      label: 'goods.pricing.form.fields.remarks',
      controlName: 'description',
      type: 'textarea',
      placeholder: 'goods.pricing.form.placeholders.enter_remarks',
      maxLength: 500,
      section: 'goods.pricing.form.sections.basic_information'
    }
  ];

  ngOnInit(): void {
    this.loading = true;

    // Set static dropdown options for price code
    const staticOptions = PRICE_CODE_OPTIONS;

    // Update form fields with static options
    const idx = this.formFields.findIndex(f => f.controlName === 'value');
    if (idx !== -1) {
      this.formFields = [
        ...this.formFields.slice(0, idx),
        { ...this.formFields[idx], options: staticOptions },
        ...this.formFields.slice(idx + 1)
      ];
    }

    // Get the ID from route parameters
    this.route.params.subscribe(params => {
      const id = params['id'];
      if (id) {
        // Load the item data
        this.service.getPriceItemById(Number(id)).subscribe({
                     next: (item) => {
             if (item) {
               const derivedKey = this.service.getValueMeaningFromLov(item.value);
               // Translate the i18n key to the actual translated value
               const translatedValue = derivedKey ? this.translateService.instant(derivedKey) : '';
               const formData = {
                 value: item.value,
                 valueMeaning: translatedValue,
                 meaning: item.meaning,
                 description: item.description || ''
               };
               
               // Set initial data with full item for API calls
               this.initialData = {
                 ...item,
                 ...formData
               };
               
               // Try to patch form immediately if it's ready
               if (this.addForm && this.addForm.form) {
                 this.addForm.form.patchValue(formData);
               }
             } else {
               // Handle case where item is not found
               console.error('Price item not found with ID:', id);
               this.navigateBack();
             }
             this.loading = false;
           },
          error: (error) => {
            console.error('Edit component - Error getting price item:', error);
            this.loading = false;
            this.navigateBack();
          }
        });
      } else {
        // Handle case where no ID is provided
        console.error('No ID provided for editing');
        this.navigateBack();
        this.loading = false;
      }
    });
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'cancel') {
      window.history.back();
      return;
    }
    if (event.action === 'save' && event.data && this.initialData) {
      const raw = event.data;
      const payload: PriceItem = {
        ...this.initialData,
        value: raw.value,
        meaning: raw.meaning,
        description: raw.description || '',
        lastUpdateDate: new Date().toISOString(),
        objectVersionNumber: (this.initialData.objectVersionNumber || 1) + 1,
      };
      this.loading = true;
      this.service.updatePriceItem(payload).subscribe((success) => {
        this.loading = false;
        if (success) {
          this.skipCanDeactivate = true;
          this.addForm?.form?.markAsPristine();
          this.navigateBack();
          return;
        } else {
          // Handle error - could show a toast message here
          console.error('Failed to update price item');
        }
      });
    }
  }

  onFormValueChanges(value: any): void {
    const val = value?.value;
    if (!this.addForm || !val) return;
    const derivedKey = this.service.getValueMeaningFromLov(val);
    if (derivedKey) {
      // Translate the i18n key to the actual translated value
      const translatedValue = this.translateService.instant(derivedKey);
      const current = this.addForm.form.get('valueMeaning')?.value;
      if (translatedValue && translatedValue !== current) {
        this.addForm.form.get('valueMeaning')?.setValue(translatedValue);
      }
    }
  }

  ngAfterViewInit(): void {
    // If we have data but the form wasn't ready when it was set, try to patch it now
    if (this.initialData && this.addForm && this.addForm.form) {
      // Translate valueMeaning if it's an i18n key
      let valueMeaning = this.initialData.valueMeaning;
      if (valueMeaning && valueMeaning.includes('goods.pricing.price_types.')) {
        valueMeaning = this.translateService.instant(valueMeaning);
      }
      // Only patch the form fields, not the entire initialData object
      const formData = {
        value: this.initialData.value,
        valueMeaning: valueMeaning,
        meaning: this.initialData.meaning,
        description: this.initialData.description || ''
      };
      this.addForm.form.patchValue(formData);
    }
  }

  private navigateBack(): void {
    // Get the current query parameters from the route
    const currentQueryParams = this.route.snapshot.queryParams;
    
    // Navigate back to the list page with preserved query parameters
    this.router.navigate(['/goods/product-pricing'], {
      queryParams: currentQueryParams
    });
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading) {
      return true;
    }

    return !(this.addForm?.form?.dirty ?? false);
  }
}


